import type { FileMap, FileMapEntry } from '.';
import { invariant } from '~/utils/invariant';

interface FileMapCache {
  currentFileMap: FileMap;
}

export function fileMapCache(): FileMapCache {
  return {
    currentFileMap: {},
  };
}

/**
 * Filter a FileMap and generate another FileMap that only contains entries that matched
 * the provided predicate.
 *
 * By default if a folder matches, it includes all its descendants unless they are explicitly excluded.
 *
 * @param filemap The FileMap to filter.
 * @param predicate Function to evaluate on every entry to test whether or not they should be included.
 */
export function filterFileMap(
  filemap: FileMap,
  predicate: (value: FileMapEntry, parentMatched: boolean, fullPath: string) => boolean | undefined,
  cache?: FileMapCache,
): FileMap {
  const folderStates: { folderPath: string; matched: boolean }[] = [];

  const filteredFiles: FileMap = {};

  for (const [fullPath, fileMapEntry] of sortFileMapEntries(filemap)) {
    let parentMatched = false;

    while (folderStates.length > 0) {
      const { folderPath, matched } = folderStates[folderStates.length - 1];

      if (fullPath.startsWith(folderPath)) {
        parentMatched = matched;
        break;
      }

      folderStates.pop();
    }

    const matched = !!predicate(fileMapEntry, parentMatched, fullPath);

    if (matched) {
      filteredFiles[fullPath] = fileMapEntry;
    }

    if (fileMapEntry?.type === 'folder') {
      folderStates.push({
        folderPath: fullPath + '/',
        matched,
      });
    }
  }

  if (!cache) {
    return filteredFiles;
  }

  const newFilePaths = new Set(Object.keys(filteredFiles));
  const cachedFilePaths = new Set(Object.keys(cache.currentFileMap));

  if (newFilePaths.size !== cachedFilePaths.size) {
    cache.currentFileMap = filteredFiles;

    return filteredFiles;
  }

  // check if there are any changes
  for (const filePath in filteredFiles) {
    const file = filteredFiles[filePath];
    const prevFile = cache.currentFileMap[filePath];

    // if a file was removed or it's type was changed
    if (typeof file !== typeof prevFile || file?.type !== prevFile?.type) {
      cache.currentFileMap = filteredFiles;

      return filteredFiles;
    }

    // at this point both type are identical we only care about files
    if (file?.type === 'file') {
      invariant(prevFile?.type === 'file');

      // if the file content has changed then we invalidate the cache
      if (prevFile.content !== file.content) {
        cache.currentFileMap = filteredFiles;

        return filteredFiles;
      }
    }
  }

  return cache.currentFileMap;
}

function sortFileMapEntries<T>(filemap: Record<string, T>): [string, T][] {
  const entries = Object.entries(filemap);

  entries.sort((a, b) => a[0].localeCompare(b[0]));

  return entries;
}
